import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Icon } from '@/features/common/Icon';
import { publicSearchApi } from '@/features/publicsite/api/publicSearchApi';
import { useLanguage } from '@/i18n/LanguageContext';
import { publicLayout } from '@/i18n/dictionaries/publicLayout';
import { publicSearch } from '@/i18n/dictionaries/publicSearch';
import { searchTypeKey } from '@/features/publicsite/searchTypes';
import { useTranslation } from '@/i18n/useTranslation';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';
import type { SearchHit } from '@/features/publicsite/types/SearchHit';

export function SearchPage() {
  const { t } = useTranslation(publicSearch);
  const { t: tc } = useTranslation(publicLayout);
  usePublicPageTitle(t('heading'));
  const { language } = useLanguage();
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const [draft, setDraft] = useState(query);
  const [hits, setHits] = useState<SearchHit[] | null>(null);

  useEffect(() => setDraft(query), [query]);

  useEffect(() => {
    if (!query.trim()) {
      setHits(null);
      return;
    }
    let live = true;
    setHits(null);
    publicSearchApi.search(query).then((r) => {
      if (live) setHits(r.items);
    });
    return () => {
      live = false;
    };
  }, [query, language]);

  return (
    <section className="container section">
      <h1>{t('heading')}</h1>

      {/* The same control as the header's, in its page variant — a labelled submit rather than the
          header's icon-only circle, because on a page with no navigation beside it an unlabelled
          arrow reads as "back" rather than "search". */}
      <form
        role="search"
        className="search-input search-input--page rectangular medium"
        onSubmit={(e) => {
          e.preventDefault();
          setParams(draft ? { q: draft } : {});
        }}
      >
        <label className="visually-hidden" htmlFor="q">
          {t('heading')}
        </label>
        <span className="icon-search" aria-hidden="true">
          <Icon name="search" className="icon-search__glyph" />
        </span>
        <input
          id="q"
          className="input"
          name="q"
          type="search"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={tc('commonSearchPlaceholder')}
        />
        <button type="submit" className="btn btn--search">
          {tc('navSearchButton')}
        </button>
      </form>

      {query.trim() && hits && hits.length > 0 && (
        <>
          <p className="search-summary">
            {hits.length} {t('resultsSummary')} „{query}”
          </p>
          <ul className="search-results card-grid">
            {hits.map((h) => (
              <li key={`${h.typeKey}-${h.entityId}`}>
                {/* An in-app route for everything the migration has reached, so a result opens
                    without a full page load; the router falls through to the server for the
                    routes still rendered by Thymeleaf. */}
                <Link className="card" to={h.url}>
                  <span className="badge">{t(searchTypeKey(h.typeKey))}</span>
                  <h3>{h.title}</h3>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      {query.trim() && hits && hits.length === 0 && (
        <div className="empty-state empty-state--search">
          <Icon name="search" className="empty-state__glyph" />
          <p className="empty-state__title">
            {t('noResultsPrefix')} „{query}”. {t('noResultsRetry')}
          </p>
          <p className="empty-state__hint">{t('noResultsHint')}</p>
          <div className="chip-row">
            <Link className="chip" to="/catalog">
              {tc('navCatalog')}
            </Link>
            <Link className="chip" to="/instruiri">
              {tc('navTrainingsShort')}
            </Link>
            <Link className="chip" to="/stiri">
              {tc('navNewsShort')}
            </Link>
          </div>
        </div>
      )}
    </section>
  );
}
